import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("light", "kana");

type Locale = "ko" | "en";

const LOCALE_STORAGE_KEY = "kanatomo-about-locale";
const locales: Locale[] = ["ko", "en"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN" };

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
    contactTitle: string;
    contactBody: string;
  }
> = {
  ko: {
    crumb: "KanaTomo About",
    title: "KanaTomo,",
    accentTitle: "예문으로 익히는 일본어.",
    subtitle:
      "JLPT N1~N5 단어를 예문, 발음, 퀴즈, 복습 흐름 안에서 차분하게 익히는 일본어 학습 앱입니다. 단순 암기보다 문맥과 리듬을 통해 오래 남는 학습 경험을 만드는 데 집중했습니다.",
    sections: [
      {
        title: "학습 목표",
        body:
          "KanaTomo는 JLPT(N1~N5) 공식 등급 단어를 체계적으로 학습할 수 있도록 설계된 일본어 단어 학습 앱입니다. 단어, 예문, 발음, 복습 기능을 하나로 통합하여 매일 짧은 시간만 투자해도 누적 학습이 이어지도록 구성했습니다.",
      },
      {
        title: "주요 기능",
        list: [
          "JLPT N1~N5 레벨별 학습 모드",
          "자연스러운 문맥의 예문 학습",
          "원어민 발음 지원 (TTS)",
          "자동 스크롤 / 퀴즈 / 복습 시스템",
          "히라가나·가타카나 쓰기 연습 기능",
          "학습 진도 관리 및 백업/복원",
        ],
      },
      {
        title: "차별점",
        body:
          "단순 암기형 앱이 아닌, 문맥 기반 학습을 중심으로 설계되었습니다. JLPT 시험 준비뿐 아니라 실제 회화와 독해 감각까지 함께 키울 수 있는 조용한 개인 학습 도구를 목표로 합니다.",
      },
    ],
    contactTitle: "문의",
    contactBody: "운영사 Daydream Factory에 대한 서비스 제안, 협업 문의, 오류 제보는 아래 이메일로 연락해주세요.",
  },
  en: {
    crumb: "KanaTomo About",
    title: "KanaTomo,",
    accentTitle: "Japanese through examples.",
    subtitle:
      "KanaTomo is a quiet Japanese learning app built around JLPT N1-N5 vocabulary, example sentences, pronunciation, quizzes, and review loops. It is designed to help words stay with you through context and rhythm, not memorization alone.",
    sections: [
      {
        title: "Learning Goal",
        body:
          "KanaTomo is designed to help learners build Japanese vocabulary systematically across JLPT N1-N5 levels. Words, example sentences, audio, and review tools are connected in one flow so even short daily sessions keep building over time.",
      },
      {
        title: "Key Features",
        list: [
          "JLPT N1-N5 level-based study modes",
          "Example-sentence learning in natural context",
          "Native-style pronunciation support (TTS)",
          "Auto-scroll, quiz, and review system",
          "Hiragana and katakana writing practice",
          "Study progress management with backup and restore",
        ],
      },
      {
        title: "What Makes It Different",
        body:
          "KanaTomo is not built as a simple memorization app. It focuses on context-based learning so learners can prepare for JLPT while also developing a better feel for real reading and conversation.",
      },
    ],
    contactTitle: "Contact",
    contactBody: "For suggestions, collaboration inquiries, or bug reports to Daydream Factory, please reach out by email below.",
  },
};

const About: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("ko");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "ko"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        {copy.sections.map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? <p style={styles.body}>{section.body}</p> : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.contactTitle}</h2>
          <div style={styles.sectionCard}>
            <p style={styles.body}>
              {copy.contactBody}
              <br />
              <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                support@daydreamfactory.net
              </a>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default About;
